import AccessPolicy from '../enums/accessPolicy';
import CalculationAction from '../enums/calculationAction';
import ClientStatus from '../enums/clientStatus';
import Role from '../enums/role';
import UserStatus from '../enums/userStatus';
import { getCalculationActions } from '../mappers/calculationAction/calculationActionsMap';
import store from '../store';
class AccessPolicyService {
    constructor() {
        this.authStore = store.auth;
        this.calculationDetailsStore = store.calculationDetails;
        this.accessPolicyHandlers = {
            [AccessPolicy.ViewKpmgAdmins]: () => this.hasRole(Role.KpmgAdmin),
            [AccessPolicy.ViewClientAdmins]: () => this.hasRole(Role.KpmgAdmin),
            [AccessPolicy.ViewCompanies]: () => this.hasAtLeastOneRole([
                Role.ClientAdmin,
                Role.Creator,
                Role.Approver,
                Role.Verifier,
                Role.Universal,
                Role.Watcher
            ]),
            [AccessPolicy.ViewUsers]: () => this.hasAtLeastOneRole([
                Role.ClientAdmin,
                Role.Creator,
                Role.Approver,
                Role.Verifier,
                Role.Universal,
                Role.Watcher
            ]),
            [AccessPolicy.AddCompany]: () => this.hasActiveRole(Role.ClientAdmin),
            [AccessPolicy.AddUser]: () => this.hasActiveRole(Role.ClientAdmin),
            [AccessPolicy.EditCompany]: (clientId) => this.isActiveAdminForClient(clientId),
            [AccessPolicy.EditClient]: (clientStatus) => ![
                ClientStatus.Deleted,
                ClientStatus.Inactive,
                ClientStatus.ReadOnly
            ].includes(clientStatus) && this.hasRole(Role.KpmgAdmin),
            [AccessPolicy.ViewCalculations]: () => this.hasAtLeastOneRole([
                Role.Universal,
                Role.Creator,
                Role.Verifier,
                Role.Approver,
                Role.Watcher
            ]),
            [AccessPolicy.AddCalculation]: () => this.hasAtLeastOneActiveRole([
                Role.Universal,
                Role.Creator
            ]),
            [AccessPolicy.EditUser]: () => this.hasActiveRole(Role.ClientAdmin),
            [AccessPolicy.ViewDocuments]: () => this.hasAtLeastOneRole([
                Role.Universal,
                Role.Creator,
                Role.Verifier,
                Role.Approver,
                Role.Watcher
            ]),
            [AccessPolicy.AddDocuments]: () => this.hasAtLeastOneActiveRole([
                Role.Universal,
                Role.Creator,
                Role.Verifier,
                Role.Approver
            ]),
            [AccessPolicy.ViewHistoricalModule]: ({ companyId, numberOfCalculations }) => (this.hasRoleForCompany([
                Role.Universal,
                Role.Creator,
                Role.Watcher,
                Role.Approver,
                Role.Verifier
            ], companyId) && numberOfCalculations > 0),
            [AccessPolicy.EditHistoricalModule]: (companyId) => this.hasActiveRoleForCompany([
                Role.Universal,
                Role.Creator
            ], companyId),
            [AccessPolicy.AddComments]: () => this.hasAtLeastOneActiveRole([
                Role.Universal,
                Role.Creator,
                Role.Verifier,
                Role.Approver
            ]),
            [AccessPolicy.EditCalculation]: () => this.canEditCalculation(),
            [AccessPolicy.ViewCalculation]: () => this.canViewCalculation(),
            [AccessPolicy.ViewCompany]: ({ clientId, companyId }) => this.hasExactRoleForCompany(Role.Watcher, companyId) &&
                !this.isAdminForClient(clientId),
            [AccessPolicy.ViewUser]: () => this.hasRole(Role.Watcher) && !this.hasRole(Role.ClientAdmin),
            [AccessPolicy.DeleteDocument]: (companyId) => this.hasActiveRoleForCompany([
                Role.Universal,
                Role.Creator,
                Role.Approver,
                Role.Verifier
            ], companyId),
            [AccessPolicy.DeleteCompany]: (clientId) => this.isActiveAdminForClient(clientId),
            [AccessPolicy.ViewNotifications]: () => this.hasAtLeastOneRole([
                Role.Universal,
                Role.Creator,
                Role.Approver,
                Role.Verifier,
                Role.Watcher
            ])
        };
        this.canViewCalculation = () => {
            const calculationDetails = this.calculationDetailsStore.getCalculationDetails();
            if (!calculationDetails.companyId)
                return true;
            return this.hasRoleForCompany([
                Role.Universal,
                Role.Creator,
                Role.Verifier,
                Role.Approver,
                Role.Watcher
            ], calculationDetails.companyId);
        };
        this.canEditCalculation = () => {
            const associations = this.authStore.getCurrentUserClientCompanyAssociations();
            const userEmail = this.authStore.getCurrentUser().email;
            const calculationDetails = this.calculationDetailsStore.getCalculationDetails();
            if (!associations || !calculationDetails.companyId)
                return true;
            const companyAssociation = associations.find(item => item.companyId === calculationDetails.companyId);
            if (companyAssociation.roleStatusId != UserStatus.Active)
                return false;
            const companyRole = companyAssociation.roleId;
            const actions = getCalculationActions({
                userIsAssigned: calculationDetails.activeUserEmail === userEmail,
                userRole: companyRole,
                calculationBaseDetails: calculationDetails
            });
            if (actions.includes(CalculationAction.Edit))
                return true;
            return false;
        };
        this.hasRole = (role) => this.authStore.getCurrentUserRoles().includes(role);
        this.hasActiveRole = (role) => this.authStore
            .getCurrentUserClientCompanyAssociations().some((assoc) => assoc.roleStatusId == UserStatus.Active && assoc.roleId == role);
        this.hasAtLeastOneRole = (roles) => this.authStore.getCurrentUserRoles().some((role) => roles.includes(role));
        this.hasAtLeastOneActiveRole = (roles) => this.authStore
            .getCurrentUserClientCompanyAssociations()
            .some((association) => association.roleStatusId == UserStatus.Active && roles.includes(association.roleId));
        this.hasRoleForCompany = (roles, companyId) => this.authStore
            .getCurrentUserClientCompanyAssociations()
            .some((association) => association.companyId === companyId && roles.includes(association.roleId));
        this.hasActiveRoleForCompany = (roles, companyId) => this.authStore
            .getCurrentUserClientCompanyAssociations()
            .some((association) => association.companyId === companyId &&
            association.roleStatusId == UserStatus.Active &&
            roles.includes(association.roleId));
        this.hasExactRoleForCompany = (role, companyId) => this.authStore
            .getCurrentUserClientCompanyAssociations()
            .some((association) => association.companyId === companyId && association.roleId === role);
        this.isAdminForClient = (clientId) => this.authStore
            .getCurrentUserClientCompanyAssociations()
            .some((association) => association.clientId === clientId && association.roleId === Role.ClientAdmin);
        this.isActiveAdminForClient = (clientId) => this.authStore
            .getCurrentUserClientCompanyAssociations()
            .some((association) => association.roleStatusId == UserStatus.Active &&
            association.clientId === clientId &&
            association.roleId === Role.ClientAdmin);
        this.hasAccess = (policy) => policy ? this.accessPolicyHandlers[policy.id](policy.payload) : true;
    }
}
export default new AccessPolicyService();
